/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import Master from '../Master';
import IziToast from '../IziToast';
import PayModal from '../PayModal';
import ManualPaymentDetails from '../PayModal/ManualPaymentDetails';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'arrears', label: 'Arrears' },
  { value: 'over_paid', label: 'Overpaid' },
];

const STATUS_PILL = {
  // pending = uploaded but not yet locked/invoiced (operator can still delete)
  pending: { color: '#7f8c8d', bg: '#ecf0f1', label: 'Pending' },
  paid: { color: '#27ae60', bg: '#eafaf1', label: 'Paid' },
  unpaid: { color: '#c0392b', bg: '#fdecea', label: 'Unpaid' },
  arrears: { color: '#f39c12', bg: '#fff4e0', label: 'Arrears' },
  over_paid: { color: '#2980b9', bg: '#e8f4fb', label: 'Overpaid' },
};

const currency = 'KES';
// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the
// UI (e.g. "KES 109.99999999999999").
const moneyOpts = { maximumFractionDigits: 2 };
const formatMoney = (n) => {
  const v = Number(n) || 0;
  return v < 0
    ? `(${currency} ${Math.abs(v).toLocaleString(undefined, moneyOpts)})`
    : `${currency} ${v.toLocaleString(undefined, moneyOpts)}`;
};
// Same 2dp cap for non-currency numeric cells (meter readings, consumed
// units) — these are rendered raw with no formatter at all today, which
// is what let "1.0999999999999996" through onto the Water Bill table.
const formatUnits = (n) => Number(n || 0).toLocaleString(undefined, moneyOpts);

const downloadStringAsFile = (text, filename, mime = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([`﻿${text}`], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const WaterBill = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' },
  };

  // Filters
  const [period, setPeriod] = useState(moment.utc().format('YYYY-MM'));
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Data
  const [rows, setRows] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [resendingKey, setResendingKey] = useState(null);
  // ── Remark modal state ──────────────────────────────────────────────
  // `remarkRow` is the currently-selected bill row (null = modal closed).
  const [remarkRow, setRemarkRow] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);
  // ── Resend Invoice channel-picker modal state ─────────────────────
  // Lets the operator pick Email / SMS / WhatsApp per resend instead of
  // blasting all three. Default: Email + SMS on (the legacy behavior).
  const [resendRow, setResendRow] = useState(null);
  const [resendSendEmail, setResendSendEmail] = useState(true);
  const [resendSendSms, setResendSendSms] = useState(true);
  const [resendSendWhatsapp, setResendSendWhatsapp] = useState(false);
  // ── Manual "Pay" + read-only viewer modal state ─────────────────────
  const [payRow, setPayRow] = useState(null);
  const [manualViewRow, setManualViewRow] = useState(null);

  const fileInputRef = useRef(null);

  // ── Load properties on mount ───────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const propUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}properties?status=active&take=1000&order={"name":"ASC"}`
          : `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;
        const { data: { response } } = await axios.get(propUrl, requestConfig);
        const list = Array.isArray(response) ? response : [response];
        setProperties(list);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ── Load tenants when property changes ─────────────────────────────────
  useEffect(() => {
    if (!propertyId) { setTenants([]); return; }
    (async () => {
      try {
        const unitsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}units?where={"property_id":"${propertyId}"}&take=10000`,
          requestConfig,
        );
        const units = unitsRes.data.response || [];
        const unitIds = units.map((u) => u.id).join(',');
        if (!unitIds) { setTenants([]); return; }
        const { data: { response } } = await axios.get(
          `${process.env.REACT_APP_API_URL}tenants?where={"unit_id":"in::${unitIds}","status":"active"}&take=10000`,
          requestConfig,
        );
        setTenants(response || []);
      } catch (e) { setTenants([]); }
    })();
  }, [propertyId]);

  // ── Load rows + uploads whenever filters change ────────────────────────
  useEffect(() => { loadRows(); }, [period, propertyId, tenantId, statusFilter]);
  useEffect(() => { loadUploads(); }, [propertyId]);

  const loadRows = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('period', period);
      if (propertyId) params.set('property_id', propertyId);
      if (tenantId) params.set('tenant_id', tenantId);
      if (statusFilter) params.set('status', statusFilter);
      params.set('include_payment_info', 'true');
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}waterbills?${params.toString()}`,
        requestConfig,
      );
      setRows((response && response.rows) || []);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to load water bills');
      setRows([]);
    }
    setIsLoading(false);
  };

  const openRemarkModal = (row) => {
    setRemarkRow(row);
    setRemarkDraft(row.remark || '');
  };

  const closeRemarkModal = () => {
    setRemarkRow(null);
    setRemarkDraft('');
  };

  const saveRemark = async () => {
    if (!remarkRow) return;
    setRemarkSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}waterbills/remark`,
        { id: remarkRow.id, remark: remarkDraft },
        requestConfig,
      );
      IziToast(`Remark saved for House ${remarkRow.house_no}`);
      // Patch the visible row so the indicator/button update immediately
      // without waiting for a full reload.
      setRows((prev) => prev.map((r) => (
        r.id === remarkRow.id ? { ...r, remark: remarkDraft.trim() || null } : r
      )));
      closeRemarkModal();
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to save remark');
    }
    setRemarkSaving(false);
  };

  // Open the channel-picker modal for the given row. Resets the channels
  // to the legacy default (Email + SMS) so quick "send everything I used
  // to send" stays one click + Send.
  const openResendModal = (row) => {
    setResendRow(row);
    setResendSendEmail(true);
    setResendSendSms(true);
    setResendSendWhatsapp(false);
  };

  const closeResendModal = () => {
    if (resendingKey) return; // don't allow close mid-flight
    setResendRow(null);
  };

  // Resend the invoice for a single (invoiced) water bill row using the
  // channels picked in the modal.
  const submitResendInvoice = async () => {
    const row = resendRow;
    if (!row) return;
    if (!resendSendEmail && !resendSendSms && !resendSendWhatsapp) {
      IziToast('Pick at least one channel');
      return;
    }
    setResendingKey(row.id);
    const picked = [];
    if (resendSendEmail) picked.push('email');
    if (resendSendSms) picked.push('SMS');
    if (resendSendWhatsapp) picked.push('WhatsApp');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}waterbills/resend-invoice`,
        {
          id: row.id,
          send_email: resendSendEmail,
          send_sms: resendSendSms,
          send_whatsapp: resendSendWhatsapp,
        },
        requestConfig,
      );
      IziToast(`Invoice resent for House ${row.house_no} via ${picked.join(', ')}`);
      setResendRow(null);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to resend invoice');
    }
    setResendingKey(null);
  };

  // ── Manual "Pay" modal ──────────────────────────────────────────────
  const openPayModal = (row) => setPayRow(row);
  const closePayModal = () => setPayRow(null);
  const onPaySuccess = () => {
    setPayRow(null);
    loadRows();
  };
  const buildPayFormData = ({
    row, amount, mode, transactionDetails, file,
  }) => {
    const fd = new FormData();
    fd.append('invoice_id', row.invoice_id);
    fd.append('amount', amount);
    fd.append('mode', mode);
    fd.append('transaction_details', transactionDetails || '');
    if (file) fd.append('proof', file);
    return fd;
  };

  const loadUploads = async () => {
    try {
      const params = propertyId ? `?property_id=${propertyId}` : '';
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}waterbills/uploads${params}`,
        requestConfig,
      );
      setUploads(response || []);
    } catch (e) { setUploads([]); }
  };

  // ── Download CSV template ──────────────────────────────────────────────
  const downloadTemplate = async () => {
    if (!propertyId) {
      IziToast('Select a property first to download its template');
      return;
    }
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}waterbills/template?property_id=${propertyId}&period=${period}`,
        requestConfig,
      );
      if (response && response.csv) {
        downloadStringAsFile(response.csv, response.file_name || `water-template-${period}.csv`);
      } else {
        IziToast('No template generated');
      }
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to download template');
    }
  };

  // ── Upload CSV ─────────────────────────────────────────────────────────
  const onFileSelected = async (e) => {
    // Capture file + name BEFORE any await — React nullifies e.target afterwards.
    const file = e.target.files && e.target.files[0];
    const resetInput = () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    if (!file) return;
    if (!propertyId) {
      IziToast('Select a property first');
      resetInput();
      return;
    }
    setIsUploading(true);
    try {
      const text = await file.text();
      await axios.post(
        `${process.env.REACT_APP_API_URL}waterbills/upload`,
        {
          property_id: propertyId,
          period,
          file_name: file.name,
          csv: text,
        },
        requestConfig,
      );
      IziToast('Water bill uploaded successfully');
      loadRows();
      loadUploads();
    } catch (err) {
      IziToast(err.response?.data?.error || 'Failed to upload water bill');
    }
    resetInput();
    setIsUploading(false);
  };

  // ── Delete / re-download upload ─────────────────────────────────────────
  const deleteUpload = async (upload) => {
    if (upload.locked) {
      IziToast('This upload is locked and cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete upload for ${upload.property_name} (${upload.period})?`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}waterbills/uploads/${upload.id}`,
        requestConfig,
      );
      IziToast('Upload deleted');
      loadUploads();
      loadRows();
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to delete upload');
    }
  };

  const downloadUpload = async (upload) => {
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}waterbills/uploads/${upload.id}`,
        requestConfig,
      );
      if (response && response.csv) {
        downloadStringAsFile(response.csv, response.file_name);
      } else {
        IziToast('No CSV data stored for this upload');
      }
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to download upload');
    }
  };

  // ── Filtered rows for client-side search ───────────────────────────────
  const visibleRows = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.property_name || '').toLowerCase().includes(q)
      || (r.house_no || '').toLowerCase().includes(q)
      || (r.tenant_name || '').toLowerCase().includes(q)
    );
  });

  // ── Pending total (sum of positive balances visible) ────────────────────
  const total = visibleRows.reduce((s, r) => s + (Number(r.balance) > 0 ? Number(r.balance) : 0), 0);
  const periodLabel = moment.utc(`${period}-01`).format('MMMM YYYY');

  // Past period = read-only (no uploads, no deletes)
  const currentPeriod = moment.utc().format('YYYY-MM');
  const isPastPeriod = period < currentPeriod;

  // ── CSV export of current table ────────────────────────────────────────
  const exportRowsCSV = () => {
    if (visibleRows.length === 0) {
      IziToast('No rows to export');
      return;
    }
    const headers = [
      'No.', 'Property', 'House No.', 'Current Reading', 'Previous Reading',
      'Consumed Units', 'Rate per Unit', 'Current Balance', 'Arrears',
      'Amount Due', 'Amount Paid', 'Balance', 'Status',
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(',')];
    visibleRows.forEach((r, i) => {
      lines.push([
        i + 1, r.property_name || '', r.house_no || '',
        r.current_reading, r.previous_reading, r.consumed_units,
        r.rate_per_unit, r.current_balance, r.arrears,
        r.amount_due, r.amount_paid, r.balance,
        STATUS_PILL[r.status] ? STATUS_PILL[r.status].label : r.status,
      ].map(esc).join(','));
    });
    downloadStringAsFile(lines.join('\n'), `water-bills-${period}.csv`);
  };

  return (
    <Master redirectLink="waterbill" title="Finance" source="Water Bill">
      <input
        type="file"
        accept=".csv,text/csv"
        ref={fileInputRef}
        onChange={onFileSelected}
        style={{ display: 'none' }}
      />

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>
          Water Bill &nbsp;
          <span style={{ fontSize: 14, color: '#888' }}>For the Month:</span>{' '}
          <span style={{ color: '#c0392b' }}>{periodLabel}</span>
        </h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading || !propertyId || isPastPeriod}
            title={
              !propertyId
                ? 'Select a property first'
                : isPastPeriod
                  ? 'Uploads are not allowed for past months'
                  : 'Upload CSV'
            }
          >
            <i className="fa fa-upload" style={{ marginRight: 6 }} />
            {isUploading ? 'Uploading...' : 'Upload CSV'}
          </button>
          <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: '8px 16px', minWidth: 160, textAlign: 'right' }}>
            <div style={{ color: '#999', fontSize: 12, fontWeight: 'bold' }}>Pending Total</div>
            <div style={{ color: '#c0392b', fontSize: 22, fontWeight: 'bold' }}>
              {currency} {total.toLocaleString(undefined, moneyOpts)}
            </div>
          </div>
        </div>
      </div>

      {isPastPeriod && (
        <div
          style={{
            background: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: 4,
            padding: '10px 14px',
            marginBottom: 12,
            color: '#7a5d00',
            fontSize: 13,
          }}
        >
          <i className="fa fa-lock" style={{ marginRight: 6, color: '#f39c12' }} />
          You are viewing <strong>{periodLabel}</strong> — a past period.
          Water bill records are read-only. New uploads and deletions are not allowed.
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: 11, color: '#666', textAlign: 'right' }}>
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); downloadTemplate(); }}
          style={{ color: '#c0392b', marginRight: 12 }}
        >
          <i className="fa fa-download" /> Download CSV Template
        </a>
        <span>(select a property first)</span>
      </div>

      {/* Filters */}
      <div className="row" style={{ marginBottom: 16 }}>
        <div className="col-md-2">
          <label className="control-label">Period</label>
          <input
            type="month"
            className="form-control"
            value={period}
            onChange={(e) => setPeriod(e.target.value || moment.utc().format('YYYY-MM'))}
          />
        </div>
        <div className="col-md-3">
          <label className="control-label">Select Property</label>
          <select
            className="form-control"
            value={propertyId}
            onChange={(e) => {
              setPropertyId(e.target.value);
              setTenantId('');
            }}
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="control-label">Select Tenant</label>
          <select
            className="form-control"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            disabled={!propertyId}
          >
            <option value="">All tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {`${t.first_name} ${t.last_name} — ${
                  t.other_details && t.other_details.unit_number ? t.other_details.unit_number : '-'
                }`}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="control-label">Payment Status</label>
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <h4 style={{ marginTop: 18 }}>Water Bill Payment</h4>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <button type="button" className="btn btn-default btn-sm" onClick={exportRowsCSV}>
            <i className="fa fa-file-text-o" /> CSV
          </button>
          <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
            Showing <strong>{visibleRows.length}</strong> of {rows.length}
          </span>
        </div>
        <div>
          <label style={{ fontSize: 13, marginRight: 8 }}>Search:</label>
          <input
            type="text"
            className="form-control"
            style={{ display: 'inline-block', width: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>No.</th>
              <th>Property</th>
              <th>House No.</th>
              <th>Current Reading</th>
              <th>Previous Reading</th>
              <th>Consumed Units</th>
              <th>Rate per Unit</th>
              <th>Current Balance</th>
              <th>Arrears</th>
              <th>Amount Due</th>
              <th>Amount Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan="14" style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading...</td></tr>
            )}
            {!isLoading && visibleRows.length === 0 && (
              <tr><td colSpan="14" style={{ textAlign: 'center', padding: 20, color: '#888' }}>No water bill records for this filter.</td></tr>
            )}
            {!isLoading && visibleRows.map((r, idx) => {
              const pill = STATUS_PILL[r.status] || { color: '#555', bg: '#eee', label: r.status };
              return (
                <tr key={r.id}>
                  <td>{idx + 1}</td>
                  <td>{r.property_name || '-'}</td>
                  <td>{r.house_no}</td>
                  <td>{formatUnits(r.current_reading)}</td>
                  <td>{formatUnits(r.previous_reading)}</td>
                  <td>{formatUnits(r.consumed_units)}</td>
                  <td>{formatMoney(r.rate_per_unit)}</td>
                  <td>{formatMoney(r.current_balance)}</td>
                  <td>{formatMoney(r.arrears)}</td>
                  <td>{formatMoney(r.amount_due)}</td>
                  <td>{formatMoney(r.amount_paid)}</td>
                  <td style={{ color: r.balance > 0 ? '#c0392b' : r.balance < 0 ? '#2980b9' : '#27ae60', fontWeight: 'bold' }}>
                    {formatMoney(r.balance)}
                  </td>
                  <td>
                    <span
                      style={{
                        background: pill.bg, color: pill.color,
                        padding: '3px 10px', borderRadius: 12,
                        fontSize: 11, fontWeight: 'bold',
                      }}
                    >
                      {pill.label}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(r.status === 'unpaid' || r.status === 'arrears') && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => openResendModal(r)}
                          disabled={resendingKey === r.id}
                          title="Resend the water bill invoice to the tenant"
                        >
                          <i className="fa fa-paper-plane" style={{ marginRight: 4 }} />
                          {resendingKey === r.id ? 'Sending...' : 'Resend Invoice'}
                        </button>
                      )}
                      {(r.status === 'unpaid' || r.status === 'arrears') && r.invoice_id && (
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => openPayModal(r)}
                          title="Record a manual payment for this bill"
                        >
                          <i className="fa fa-money" style={{ marginRight: 4 }} />
                          Pay
                        </button>
                      )}
                      {r.status === 'paid' && r.payment_channel === 'manual' && (
                        <button
                          type="button"
                          className="btn btn-info btn-sm"
                          onClick={() => setManualViewRow(r)}
                          title="View manual payment details"
                        >
                          <i className="fa fa-eye" style={{ marginRight: 4 }} />
                          Manual
                        </button>
                      )}
                      <button
                        type="button"
                        className={`btn btn-sm ${r.remark ? 'btn-info' : 'btn-default'}`}
                        onClick={() => openRemarkModal(r)}
                        title={r.remark ? 'View / edit remark' : 'Add a remark for this bill'}
                      >
                        <i
                          className={`fa ${r.remark ? 'fa-sticky-note' : 'fa-sticky-note-o'}`}
                          style={{ marginRight: 4 }}
                        />
                        Remark
                      </button>
                      {r.invoice_id && (
                        <button
                          type="button"
                          className="btn btn-default btn-sm"
                          title="View PDF invoice"
                          onClick={() => {
                            const ref = `INV-${String(r.invoice_id).substring(0, 10).toUpperCase()}`;
                            const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                            window.open(`${base}/public/invoice.pdf?ref=${ref}`, '_blank');
                          }}
                        >
                          <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                          PDF
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Upload log */}
      <h4 style={{ marginTop: 28 }}>CSV Upload Log</h4>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>No.</th>
              <th>Time — Uploaded</th>
              <th>Property</th>
              <th>Uploaded by</th>
              <th>Period</th>
              <th>Total Units</th>
              <th>Consumed Units</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {uploads.length === 0 && (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: 20, color: '#888' }}>No CSV uploads yet.</td></tr>
            )}
            {uploads.map((u, idx) => (
              <tr key={u.id}>
                <td>{idx + 1}</td>
                <td>{moment(u.createdat).format('D/M/YYYY - HH:mm')}</td>
                <td>{u.property_name || '-'}</td>
                <td>{u.uploaded_by_name || '-'}</td>
                <td>{u.period}</td>
                <td>{u.total_units}</td>
                <td>{formatUnits(u.consumed_units)}</td>
                <td>{formatMoney(u.total_amount)}</td>
                <td>
                  {u.locked
                    ? <span style={{ background: '#eafaf1', color: '#27ae60', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 'bold' }}>Locked</span>
                    : <span style={{ background: '#fff4e0', color: '#f39c12', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 'bold' }}>Editable</span>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const uploadPast = u.period < currentPeriod;
                    if (u.locked || uploadPast) {
                      const reason = u.locked
                        ? 'Locked uploads cannot be deleted'
                        : 'Past-month uploads are read-only';
                      return (
                        <button
                          type="button"
                          className="btn btn-default btn-sm"
                          disabled
                          title={reason}
                          style={{ marginRight: 4 }}
                        >
                          Delete
                        </button>
                      );
                    }
                    return (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteUpload(u)}
                        style={{ marginRight: 4 }}
                      >
                        Delete
                      </button>
                    );
                  })()}
                  <button type="button" className="btn btn-default btn-sm" onClick={() => downloadUpload(u)}>
                    <i className="fa fa-download" /> Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Resend Invoice channel-picker modal ──────────────────────── */}
      {resendRow && (
        <div
          onClick={closeResendModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              width: 'min(420px, 92vw)',
              borderRadius: 6,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                {`Resend water bill for House ${resendRow.house_no || ''}`}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Pick the channels you want to notify on.
              </div>
            </div>
            <div style={{ padding: '14px 20px' }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={resendSendEmail}
                  onChange={(e) => setResendSendEmail(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send Email
              </label>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={resendSendSms}
                  onChange={(e) => setResendSendSms(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send SMS
              </label>
              <label style={{ display: 'block', fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={resendSendWhatsapp}
                  onChange={(e) => setResendSendWhatsapp(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send WhatsApp
              </label>
            </div>
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={closeResendModal}
                disabled={resendingKey !== null}
                style={{
                  border: '1px solid #ccc',
                  background: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: resendingKey !== null ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitResendInvoice}
                disabled={resendingKey !== null}
                style={{
                  border: '1px solid var(--red_color, #d63835)',
                  background: 'var(--red_color, #d63835)',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: resendingKey !== null ? 'not-allowed' : 'pointer',
                }}
              >
                {resendingKey !== null ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remark modal ───────────────────────────────────────────────── */}
      {remarkRow && (
        <div
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) closeRemarkModal(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 22,
              width: 'min(520px, 90vw)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 12,
            }}
            >
              <div>
                <h4 style={{ margin: 0, color: '#2c3e50' }}>
                  Remark — House {remarkRow.house_no}
                </h4>
                <div style={{ fontSize: 12, color: '#7c8896', marginTop: 4 }}>
                  Period {remarkRow.period}
                  {remarkRow.tenant_name ? ` · ${remarkRow.tenant_name}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={closeRemarkModal}
                style={{
                  border: 'none', background: 'none', fontSize: 22,
                  color: '#888', cursor: 'pointer', lineHeight: 1,
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <textarea
              className="form-control"
              rows={5}
              placeholder="e.g. Tenant disputed reading; leak credited next month."
              value={remarkDraft}
              onChange={(e) => setRemarkDraft(e.target.value)}
              style={{ resize: 'vertical' }}
            />
            <div style={{
              marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}
            >
              <button
                type="button"
                className="btn btn-default"
                onClick={closeRemarkModal}
                disabled={remarkSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={saveRemark}
                disabled={remarkSaving}
              >
                {remarkSaving ? 'Saving…' : 'Save Remark'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PayModal
        row={payRow}
        title="Pay Water Bill"
        subtitle={payRow ? `House ${payRow.house_no || ''} — ${periodLabel}` : ''}
        maxAmount={payRow ? Number(payRow.balance) : null}
        endpoint={`${process.env.REACT_APP_API_URL}payments/manual`}
        requestConfig={requestConfig}
        buildFormData={buildPayFormData}
        onClose={closePayModal}
        onSuccess={onPaySuccess}
      />
      <ManualPaymentDetails
        row={manualViewRow}
        amount={manualViewRow ? manualViewRow.amount_paid : null}
        onClose={() => setManualViewRow(null)}
      />
    </Master>
  );
};

const mapStateToProps = (state) => state;
WaterBill.propTypes = { user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) };
WaterBill.defaultProps = { user: {} };
export default connect(mapStateToProps)(WaterBill);
